import { Link } from 'react-router-dom';

import { Badge, ProductImage, Rating } from './ui.jsx';
import { currency, tenure } from '../lib/format.js';

/**
 * ProductCard — one tile in the catalogue grid.
 *
 * The card leads with the monthly instalment rather than the sticker price,
 * because that is the number a shopper on an EMI storefront is comparing. The
 * full price stays visible directly beneath so the framing is never misleading.
 *
 * The whole tile is a single link. Nesting further links inside would create
 * multiple tab stops for one destination, so the badges and prices are plain text.
 */
export default function ProductCard({ product, priority = false }) {
  const { slug, name, brand, tagline, heroImage, basePrice, baseMrp, discountPct, lowestEmi, hasNoCostEmi, variantCount, rating, ratingCount, inStock } = product;

  return (
    <Link
      to={`/products/${slug}`}
      className="group surface flex flex-col overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-lift focus-visible:-translate-y-1"
    >
      <div className="relative">
        <ProductImage
          src={heroImage}
          alt={name}
          priority={priority}
          className="aspect-4/3 w-full"
          imgClassName="transition-transform duration-500 group-hover:scale-105"
        />

        <div className="absolute left-3 top-3 flex flex-col items-start gap-1.5">
          {discountPct > 0 && <Badge tone="danger">{discountPct}% off</Badge>}
          {hasNoCostEmi && <Badge tone="growth">No cost EMI</Badge>}
        </div>

        {!inStock && (
          <div className="absolute inset-0 grid place-items-center bg-white/70 backdrop-blur-[2px]">
            <Badge tone="neutral">Out of stock</Badge>
          </div>
        )}
      </div>

      <div className="flex flex-1 flex-col p-4">
        {brand && <p className="text-xs font-semibold uppercase tracking-wide text-ink-500">{brand}</p>}

        <h3 className="mt-1 font-semibold leading-snug text-ink-900 group-hover:text-brand-700">{name}</h3>

        {tagline && <p className="mt-1 line-clamp-2 text-sm leading-relaxed text-ink-600">{tagline}</p>}

        {rating ? <Rating value={rating} count={ratingCount} className="mt-2.5" /> : null}

        {/* mt-auto pins the pricing block to the bottom so cards of differing
            copy length still align their prices across the grid. */}
        <div className="mt-auto pt-4">
          {lowestEmi && (
            <p className="text-lg font-bold text-ink-900">
              {currency(lowestEmi.monthlyAmount)}
              <span className="text-sm font-medium text-ink-500">/mo</span>
            </p>
          )}

          <p className="mt-0.5 text-xs text-ink-500">
            {lowestEmi ? `for ${tenure(lowestEmi.tenureMonths)} · ` : ''}
            <span className="font-semibold text-ink-700">{currency(basePrice)}</span>
            {baseMrp > basePrice && <span className="ml-1.5 line-through">{currency(baseMrp)}</span>}
          </p>

          {variantCount > 1 && (
            <p className="mt-2 text-xs font-medium text-ink-500">
              {variantCount} variants available
            </p>
          )}
        </div>
      </div>
    </Link>
  );
}

/** Matching placeholder so the grid does not reflow when real cards arrive. */
export function ProductCardSkeleton() {
  return (
    <div className="surface overflow-hidden">
      <div className="skeleton aspect-4/3 w-full rounded-none" />
      <div className="space-y-2.5 p-4">
        <div className="skeleton h-3 w-16" />
        <div className="skeleton h-4 w-3/4" />
        <div className="skeleton h-3 w-full" />
        <div className="skeleton h-6 w-28" />
        <div className="skeleton h-3 w-32" />
      </div>
    </div>
  );
}
